// v7.54 — Lógica de área de atendimento por RAIO.
//
// Antes: o cliente tinha que SELECIONAR um bairro do dropdown
// pré-cadastrado. Muitos clientes não achavam o "bairro" porque chamavam
// logradouros e sub-localidades de bairro.
//
// Agora: o cliente só digita o endereço (Google Places) e o sistema:
//   1) calcula distância em km do restaurante via Haversine
//   2) compara com `deliveryRadiusKm` da config
//   3) determina status: 'IN_AREA' | 'OUTSIDE' | 'AWAITING_CONFIRMATION'
//   4) tenta fuzzy-match com bairros pré-cadastrados pra aplicar taxa
//      conhecida; senão, marca como `AWAITING_CONFIRMATION` (cliente
//      confirma valor da entrega via WhatsApp).

/**
 * Distância em km entre dois pontos (lat/lng) via fórmula de Haversine.
 * Raio médio da Terra: 6371 km.
 */
export function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
    return null;
  }
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Normaliza string pra fuzzy match: lowercase, sem acento, sem pontuação,
 * sem palavras irrelevantes (jardim, vila, etc).
 */
function normalize(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')           // remove acentos
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')              // remove pontuação
    .replace(/\b(jardim|vila|parque|conjunto|bairro|cidade|distrito)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extrai o bairro de um Google Place result. Tenta múltiplos
 * address_components na seguinte ordem de prioridade:
 *   1. sublocality_level_1 (mais específico — "Piabetá", "Copacabana")
 *   2. sublocality
 *   3. neighborhood
 *   4. administrative_area_level_2 (fallback fraco — pode ser "Magé")
 *
 * Retorna objeto: { bairro, cidade, estado }.
 */
export function extractAddressParts(place) {
  if (!place || !Array.isArray(place.address_components)) {
    return { bairro: '', cidade: '', estado: '' };
  }
  const findType = (...types) => {
    for (const t of types) {
      const c = place.address_components.find((c) => c.types.includes(t));
      if (c) return c.long_name;
    }
    return '';
  };
  return {
    bairro: findType('sublocality_level_1', 'sublocality', 'neighborhood', 'political'),
    cidade: findType('administrative_area_level_2', 'locality'),
    estado: findType('administrative_area_level_1'),
  };
}

/**
 * Fuzzy-match: dado o nome de um bairro detectado pelo Google e a lista
 * de bairros cadastrados, retorna o bairro cadastrado equivalente OU null.
 *
 * Match positivo se:
 *   - normalize(input) === normalize(cadastrado), OU
 *   - um contém o outro (após normalize) com pelo menos 4 chars
 */
export function fuzzyMatchBairro(detectedName, cadastratedList) {
  if (!detectedName || !Array.isArray(cadastratedList) || cadastratedList.length === 0) {
    return null;
  }
  const target = normalize(detectedName);
  if (!target) return null;
  for (const nb of cadastratedList) {
    const candidate = normalize(nb?.name || '');
    if (!candidate) continue;
    if (candidate === target) return nb;
    if (target.length >= 4 && candidate.length >= 4) {
      if (candidate.includes(target) || target.includes(candidate)) return nb;
    }
  }
  return null;
}

/**
 * Avalia se o endereço selecionado pelo cliente está dentro da área
 * de atendimento. Retorna:
 *
 *   { status: 'IN_AREA_MATCHED', distanceKm, bairro: <object>, ... }
 *     → endereço dentro do raio E bairro bate com cadastrado
 *     → usa taxa de entrega do bairro
 *
 *   { status: 'IN_AREA_CONFIRM', distanceKm, detectedBairro, ... }
 *     → endereço dentro do raio MAS bairro não foi reconhecido na lista
 *     → cliente deve confirmar via WhatsApp (taxa TBD)
 *
 *   { status: 'OUTSIDE', distanceKm, ... }
 *     → fora do raio de atendimento
 *     → checkout bloqueado, mensagem gentil
 *
 *   { status: 'UNKNOWN', reason }
 *     → não foi possível avaliar (sem coordenadas, sem raio configurado)
 *     → fallback: pede confirmação manual
 */
export function evaluateDeliveryArea({
  placeLat,
  placeLng,
  placeAddressParts,
  restaurantLat,
  restaurantLng,
  deliveryRadiusKm,
  cadastratedNeighborhoods,
}) {
  if (!Number.isFinite(placeLat) || !Number.isFinite(placeLng)) {
    return { status: 'UNKNOWN', reason: 'no_place_coords' };
  }
  if (!Number.isFinite(restaurantLat) || !Number.isFinite(restaurantLng)) {
    return { status: 'UNKNOWN', reason: 'no_restaurant_coords' };
  }
  if (!Number.isFinite(deliveryRadiusKm) || deliveryRadiusKm <= 0) {
    return { status: 'UNKNOWN', reason: 'no_radius_configured' };
  }
  const distanceKm = haversineDistanceKm(placeLat, placeLng, restaurantLat, restaurantLng);
  if (distanceKm == null) {
    return { status: 'UNKNOWN', reason: 'distance_calc_failed' };
  }
  if (distanceKm > deliveryRadiusKm) {
    return {
      status: 'OUTSIDE',
      distanceKm,
      deliveryRadiusKm,
      detectedBairro: placeAddressParts?.bairro || '',
    };
  }
  // Dentro do raio — tenta match de bairro
  const matched = fuzzyMatchBairro(placeAddressParts?.bairro || '', cadastratedNeighborhoods);
  if (matched) {
    return {
      status: 'IN_AREA_MATCHED',
      distanceKm,
      deliveryRadiusKm,
      bairro: matched,
      detectedBairro: placeAddressParts?.bairro || '',
    };
  }
  return {
    status: 'IN_AREA_CONFIRM',
    distanceKm,
    deliveryRadiusKm,
    detectedBairro: placeAddressParts?.bairro || '',
  };
}
